import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import ConfirmDialog from "../../components/admin/ConfirmDialog";
import { Plus, Pencil, Trash2, Upload } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Card } from "../../components/ui/card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import PageHeader from "../../components/admin/PageHeader";
import InfoBanner from "../../components/admin/InfoBanner";
import FilterBar from "../../components/admin/FilterBar";

const BASE_URI = process.env.REACT_APP_API_BASE_URI;
const emptyForm = { image: null, imagePreview: "", author: "", date: "", title: "", excerpt: "" };

const compressImage = (file) => new Promise((resolve) => {
  const reader = new FileReader();
  reader.onload = (e) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      let [w, h] = [img.width, img.height];
      if (w > 800) { h *= 800 / w; w = 800; }
      if (h > 600) { w *= 600 / h; h = 600; }
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      canvas.toBlob((blob) => resolve(new File([blob], file.name, { type: "image/jpeg", lastModified: Date.now() })), "image/jpeg", 0.9);
    };
    img.src = e.target.result;
  };
  reader.readAsDataURL(file);
});

const BlogsCRUD = () => {
  const [blogs, setBlogs] = useState([]);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [editBlog, setEditBlog] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState("");
  const [pagination, setPagination] = useState({ page: 1, page_size: 10, total: 0, total_pages: 1 });

  const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchBlogs(); }, []);

  const fetchBlogs = async (page = 1) => {
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URI}/entities?category=blog&page=${page}&limit=${pagination.page_size}`, getAuthHeaders());
      if (res.data?.data) {
        setBlogs(res.data.data.map((i) => ({ id: i.id, image: i.data.image || "", author: i.data.author || "", date: i.data.date || "", title: i.data.title || "", excerpt: i.data.excerpt || "" })));
        setPagination({ page: res.data.page, page_size: res.data.limit, total: res.data.total, total_pages: Math.ceil(res.data.total / res.data.limit) });
      }
      setError("");
    } catch { setError("Failed to fetch blogs"); }
    finally { setLoading(false); }
  };

  const openAdd = () => { setForm(emptyForm); setEditBlog(null); setDialogOpen(true); };
  const openEdit = (b) => { setForm({ image: null, imagePreview: b.image, author: b.author, date: b.date, title: b.title, excerpt: b.excerpt }); setEditBlog(b); setDialogOpen(true); };

  const handleImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5MB"); return; }
    const compressed = await compressImage(file);
    setForm((f) => ({ ...f, image: compressed, imagePreview: URL.createObjectURL(compressed) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editBlog && !form.image) { toast.warning("Please upload a blog image"); return; }
    if (!form.title.trim() || !form.author.trim()) { toast.warning("Please fill all required fields"); return; }
    try {
      setLoading(true);
      const fd = new FormData();
      if (form.image) fd.append("images", form.image);
      fd.append("author", form.author); fd.append("date", form.date);
      fd.append("title", form.title); fd.append("excerpt", form.excerpt);
      const hdrs = { headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${localStorage.getItem("token")}` } };
      if (editBlog) {
        await axios.put(`${BASE_URI}/entities/${editBlog.id}?category=blog`, fd, hdrs);
        toast.success("Blog updated!");
      } else {
        await axios.post(`${BASE_URI}/entities?category=blog`, fd, hdrs);
        toast.success("Blog created!");
      }
      setDialogOpen(false); fetchBlogs(pagination.page);
    } catch { toast.error("Save failed"); }
    finally { setLoading(false); }
  };

  const deleteBlog = async () => {
    try {
      setDeleteLoading(true);
      await axios.delete(`${BASE_URI}/entities/${deleteId}?category=blog`, getAuthHeaders());
      toast.success("Deleted successfully!");
      setDeleteId(null);
      fetchBlogs(blogs.length === 1 && pagination.page > 1 ? pagination.page - 1 : pagination.page);
    } catch { toast.error("Failed to delete."); }
    finally { setDeleteLoading(false); }
  };

  const formatDate = (d) => { try { return d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"; } catch { return d; } };

  const filtered = blogs.filter(b => b.title.toLowerCase().includes(search.toLowerCase()) || b.author.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Blog Management"
        description="Create, edit, and manage blog posts displayed on the website"
        action={<Button onClick={openAdd}><Plus className="h-4 w-4" /> Add Blog</Button>}
      />
      <InfoBanner
        title="Blog Management"
        description="Publish articles, news, and updates. Images are automatically compressed on upload."
        capabilities={["Auto image compression", "Author & date tracking", "Excerpt for preview text"]}
      />
      <FilterBar search={search} onSearch={setSearch} placeholder="Search by title or author…" count={filtered.length} />
      {error && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[90px]">Image</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Author</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Excerpt</TableHead>
              <TableHead className="w-[120px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-gray-400">
                <div className="flex items-center justify-center gap-2"><div className="h-4 w-4 rounded-full border-2 border-blue-400 border-t-transparent animate-spin" />Loading…</div>
              </TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-gray-400">No blogs yet.</TableCell></TableRow>
            ) : filtered.map((b) => (
              <TableRow key={b.id}>
                <TableCell>
                  {b.image ? <img src={b.image} alt={b.title} className="h-14 w-20 rounded-lg object-cover border border-gray-100" onError={(e) => { e.target.style.display = "none"; }} /> : <div className="h-14 w-20 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400 text-xs">No img</div>}
                </TableCell>
                <TableCell className="font-medium text-gray-900 max-w-[180px]"><p className="line-clamp-2">{b.title}</p></TableCell>
                <TableCell className="text-gray-600 text-sm">{b.author}</TableCell>
                <TableCell className="text-gray-500 text-xs whitespace-nowrap">{formatDate(b.date)}</TableCell>
                <TableCell className="text-gray-600 text-sm max-w-[220px]"><p className="line-clamp-2">{b.excerpt}</p></TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(b)}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="destructive" onClick={() => setDeleteId(b.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-center gap-1 p-4 border-t border-gray-100">
            <Button size="sm" variant="outline" onClick={() => fetchBlogs(pagination.page - 1)} disabled={pagination.page === 1 || loading}>Previous</Button>
            {Array.from({ length: pagination.total_pages }, (_, i) => i + 1).map((p) => (
              <Button key={p} size="sm" variant={p === pagination.page ? "default" : "outline"} onClick={() => fetchBlogs(p)} disabled={loading}>{p}</Button>
            ))}
            <Button size="sm" variant="outline" onClick={() => fetchBlogs(pagination.page + 1)} disabled={pagination.page === pagination.total_pages || loading}>Next</Button>
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={!!deleteId}
        title="Delete blog?"
        description="This action cannot be undone."
        onConfirm={deleteBlog}
        onCancel={() => setDeleteId(null)}
        loading={deleteLoading}
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editBlog ? "Edit Blog" : "Add New Blog"}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 px-6 py-4">
            <div className="space-y-1.5">
              <Label>Blog Image {!editBlog && <span className="text-red-500">*</span>}</Label>
              <div className="flex items-center gap-3">
                {form.imagePreview && <img src={form.imagePreview} alt="" className="h-16 w-24 rounded-lg object-cover border border-gray-200" />}
                <label className="flex items-center gap-2 cursor-pointer rounded-lg border border-dashed border-gray-300 px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50 transition-colors">
                  <Upload className="h-4 w-4" /> {form.imagePreview ? "Change" : "Upload"} image
                  <input type="file" accept="image/*" onChange={handleImage} className="hidden" />
                </label>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Author <span className="text-red-500">*</span></Label>
                <Input placeholder="Author name" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} required />
              </div>
              <div className="space-y-1.5">
                <Label>Date</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Blog Title <span className="text-red-500">*</span></Label>
              <Input placeholder="Blog title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
            </div>
            <div className="space-y-1.5">
              <Label>Excerpt</Label>
              <Textarea placeholder="Short description…" rows={3} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} />
            </div>
          </form>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={loading}>{loading ? "Saving…" : editBlog ? "Update" : "Publish"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BlogsCRUD;
